import { clsxm } from '@app/styles/clsxm';
import { Dialog } from '@app/hoc';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';
import { FiRrCrossSmall } from '@app/assets';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';
import { ReactElement, useEffect, useState } from 'react';
import { useHandler } from '@app/hooks/useHandler.hook';
import { marketingSelectors } from '@app/gtag/marketing.constant';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Button, Checkbox } from '@app/components';
import { useMutation } from 'react-query';
import { SubscriptionApi } from '@app/domain/subscription/api/subscription.api';
import { toast } from 'react-toastify';
import { IS_PROD_ENV } from '@app/environment/typed-env';
import { EOnboardingModalType, useTrialPhasesConfig } from '@app/domain/plan/hooks/useTrialPhasesConfig';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { CreateQRCodeButton } from '@app/domain/plan/components/CreateQRCodeButton';
import { ManagePlanButton } from '@app/domain/plan/components/ManagePlanButton';
import {
  AnimatedArrowForOnboarding,
  Confetti,
  useTrialOnboardingDialogContent,
} from './hooks/useTrialOnboardingDialogContent';
import { useGetQRCodes } from '@app/domain/qr-code/api/qr-code.hooks.api';
import { useTrialOnboardingDialogState } from './hooks/useTrialOnboardingDialogState';
import { QRCodeRendererSVG, QRRendererProps } from '@app/domain/qr-code/components/QRCodeRendererSVG';
import {
  extractStylePropsFromQRDesign,
  getQRfileNameForDownload,
  getQRScanLink,
} from '@app/domain/qr-code/qr-code.utils';
import { EQRDownloadType, QRDownloadHandlerFn } from '@app/domain/qr-code/components/qr-code-internal.types';
import { buildSortParam } from '@app/utils/api.util';
import { QRCodeOutDto } from '@app/swagger-types';
import { TRIAL_ONBOARDING_ARROW_START_ELEMENT_ID, TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID } from './onboarding.constant';
import { useStore } from '@app/store/useStore.hook';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { useUpdateCustomer } from '@app/domain/user/api/user.hooks.api';

const TRIAL_WAIT_FOR_REVIEW_TIMEOUT_MS = IS_PROD_ENV ? 60e3 : 5e3;
const CAN_EXTEND_TRIAL_PERIOD = false;

export const TrialOnboardingDialog = NiceModal.create(DialogComponent);

function DialogComponent({
  trialOnboarding,
  setModalType,
}: {
  trialOnboarding: ETrialOnboardingType | null;
  setModalType?: EOnboardingModalType;
  children?: ReactElement;
}) {
  const { t } = useTranslation();
  const { modalType: configModalType } = useTrialPhasesConfig();
  const modalType = configModalType === EOnboardingModalType.NONE && setModalType ? setModalType : configModalType;
  const { currentUser } = useAuthorizedUser();
  const { visible } = useModal();
  const isMobile = useIsMobile();
  const { isTrialExtended } = useActivePlan();
  const getMe = useStore.useGetMe();

  const { mutate: updateCustomer, isLoading: isLoadingUserUpdate } = useUpdateCustomer({
    onSuccess: () => {
      openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_PRINT_AND_SHARE);
    },
  });

  const { data, isLoading } = useGetQRCodes({
    page: 0,
    sort: buildSortParam<QRCodeOutDto>('createdDate', 'desc'),
    size: 4,
  });
  const qrCode = data?.result[0];
  const rendererProps: QRRendererProps = {
    ...qrCode,
    qrId: qrCode?.id,
    ...(qrCode?.design ? extractStylePropsFromQRDesign(qrCode.design) : undefined),
    publicLogoType: qrCode?.publicLogoType,
    fileName: getQRfileNameForDownload(qrCode?.id, qrCode?.name),
    gtin: qrCode?.gs1?.gtin,
  };

  const isTimeUp = useDelayAfterRender(
    trialOnboarding === ETrialOnboardingType.TRIAL_EXTEND_TRIAL,
    TRIAL_WAIT_FOR_REVIEW_TIMEOUT_MS
  );

  const { openTrialOnboardingDialog, removeTrialOnboardingDialog, openPreviousDialogFromHistory } =
    useTrialOnboardingDialog(null);
  const { header, description, decorativeContent, shouldShowAnimatedArrow } = useTrialOnboardingDialogContent(
    trialOnboarding,
    modalType,
    {
      isLoading,
      qrCode,
      rendererProps,
    }
  );

  const [loadingType, setLoadingType] = useState<EQRDownloadType | null>(null);
  const [downloadHandler, setDownloadHandler] = useState<QRDownloadHandlerFn | undefined>(undefined);
  const onDownloadHandler = useHandler((downloadFn?: QRDownloadHandlerFn) => {
    // setter has to be a function to avoid downloadFn call
    setDownloadHandler(() => downloadFn);
  });

  useEffect(() => {
    if (!loadingType || !downloadHandler) {
      return;
    }
    let mounted = true;
    downloadHandler({ delay: 100, fileType: loadingType }).finally(() => {
      if (!mounted) {
        return;
      }
      setLoadingType(null);
      removeTrialOnboardingDialog();
    });
    return () => {
      mounted = false;
    };
  }, [downloadHandler, loadingType, removeTrialOnboardingDialog, currentUser?.anyCodeDownloaded]);

  // TODO clarify with client if we need to hide dialog after first close
  // const { mutate: updateCustomer } = useUpdateCustomer();
  // const removeTrialOnboardingDialog = useHandler(() => {
  //   if (trialOnboarding === ETrialOnboardingType.TRIAL_SCAN_CONGRATULATIONS) {
  //     updateCustomer({
  //       isFirstScanSeen: true,
  //     });
  //   }
  //   _justRemoveTrialOnboardingDialog();
  // });

  const { state, setState } = useTrialOnboardingDialogState();

  useEffect(() => {
    setState({
      ...state,
      isOpen: visible,
    });
  }, [visible, setState, state]);

  const [checked, setChecked] = useState(false);

  const extendTrialPeriodMutation = useMutation(
    async () => {
      if (!isTrialExtended) {
        await SubscriptionApi.extendTrialPeriod();
      }
      await getMe();
      removeTrialOnboardingDialog();
    },
    {
      onSuccess: async () => {
        openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_THANKS_REVIEW);
        await new Promise((resolve) => setTimeout(resolve, 3e3)); // TODO: BE long time to process
        await getMe();
      },
    }
  );

  const renderLeftSide = () => {
    let button: JSX.Element | null = null;
    const getTrialOnboardingButtonsByType = () => {
      switch (modalType) {
        case EOnboardingModalType.MODAL_2A:
          button = (
            <div className="grid mt-8 w-full gap-3 grid-cols-2 md:grid-cols-1">
              <Button variant="outlined" className="flex-1 md:hidden" onClick={removeTrialOnboardingDialog}>
                {t('qr.notNow')}
              </Button>
              <CreateQRCodeButton
                variant="contained"
                size="medium"
                onClick={removeTrialOnboardingDialog}
                fullWidth
                className="text-nowrap"
                endIcon={<></>}
                id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_ONBOADRDING)}
              >
                {t('qr.createQRCode')}
              </CreateQRCodeButton>
            </div>
          );
          break;
        case EOnboardingModalType.MODAL_2B:
          button = isMobile ? (
            <a
              href={rendererProps.qrId && getQRScanLink(rendererProps.qrId, rendererProps.gtin)}
              target="_blank"
              rel="noreferrer"
            >
              <Button className="w-full mt-4" endIcon={<ArrowForwardIosIcon className="scale-75" />}>
                {t('limit.dialog.modal_2b.simulateScan')}
              </Button>
            </a>
          ) : null;
          break;
        case EOnboardingModalType.MODAL_HIGH_INTENT_USER:
        case EOnboardingModalType.MODAL_TRIAL_EXPIRED:
          button = (
            <ManagePlanButton
              variant="contained"
              size="medium"
              fullWidth
              onCloseModal={removeTrialOnboardingDialog}
              className="mt-8"
              id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_ONBOADRDING)}
              endIcon={<ArrowForwardIosIcon />}
            >
              {t('subscription.seePlansAndUpgrade')}
            </ManagePlanButton>
          );
          break;
        case EOnboardingModalType.SCAN_CONGRATULATION:
          button = (
            <div className="flex flex-col gap-4 mt-8">
              {CAN_EXTEND_TRIAL_PERIOD && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    removeTrialOnboardingDialog();
                    openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_EXTEND_TRIAL);
                  }}
                  id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_ONBOADRDING)}
                >
                  {t('subscription.get30MorePROForFree')}
                </Button>
              )}
              <Button
                loading={isLoadingUserUpdate}
                onClick={() => {
                  updateCustomer({ isFirstScanSeen: true });
                }}
                id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_PRINT_AND_SHARE)}
              >
                {t('subscription.printShare')}
              </Button>
            </div>
          );
          break;
        case EOnboardingModalType.NONE:
        default:
          button = null;
          break;
      }
    };

    switch (trialOnboarding) {
      case ETrialOnboardingType.TRIAL_ONBOADRDING:
        getTrialOnboardingButtonsByType();
        break;
      case ETrialOnboardingType.TRIAL_EXTEND_TRIAL:
        button = (
          <div className="flex flex-col gap-4 mt-8">
            <div className="flex flex-col gap-4">
              <Checkbox
                className="flex -mt-4"
                label={<span className="text-xs text-gray-700">{t('limit.dialog.extendTrial.action')}</span>}
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
            </div>
            <Button
              onClick={() => {
                if (isTimeUp && !isTrialExtended) {
                  extendTrialPeriodMutation.mutate();
                  return;
                }
                if (!isTimeUp) {
                  toast.error(t('subscription.trialExtendError'));
                }
              }}
              disabled={!checked}
              loading={extendTrialPeriodMutation.isLoading}
            >
              {t('subscription.activate30MorePROForFree')}
            </Button>
          </div>
        );
        break;
      case ETrialOnboardingType.TRIAL_THANKS_REVIEW:
        button = (
          <Button
            onClick={() => {
              removeTrialOnboardingDialog();
              openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_PRINT_AND_SHARE);
            }}
            endIcon={<ArrowForwardIosIcon />}
            id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_THANKS_REVIEW)}
          >
            {t('subscription.printShare')}
          </Button>
        );
        break;
      case ETrialOnboardingType.TRIAL_PRINT_AND_SHARE:
        button = (
          <>
            <Button
              loading={!downloadHandler || isLoading || loadingType !== null}
              disabled={!downloadHandler || isLoading}
              onClick={() => {
                setLoadingType(EQRDownloadType.PNG);
              }}
              id={marketingSelectors.getTrialOnboardingButtonId(ETrialOnboardingType.TRIAL_PRINT_AND_SHARE)}
            >
              {t('qr.downloadQRCode')}
            </Button>
            <div className="hidden">
              <QRCodeRendererSVG {...rendererProps} onDownloadHandler={onDownloadHandler} />
            </div>
          </>
        );
        break;
      default:
        button = null;
    }

    return (
      <div className="flex flex-col h-full">
        {header}
        {description}
        {button}
      </div>
    );
  };

  const renderRightSide = () => {
    return <div className="bg-white md:pt-16 md:p-8 relative flex flex-col justify-between">{decorativeContent}</div>;
  };

  return (
    <Dialog
      open={visible}
      hideHeader
      paperClassName="text-accept-dark-blue shadow-none bg-transparent rounded-none flex items-center justify-center"
      maxWidth="md"
      fullScreen={isMobile}
    >
      {modalType === EOnboardingModalType.SCAN_CONGRATULATION && isMobile && <Confetti run />}
      <div className="flex flex-col gap-4 pt-4 md:pt-0 mx-5 md:mx-0" data-testid="plan-limit-dialog">
        <div className={clsxm('rounded-2xl bg-white shadow overflow-hidden')}>
          <div className={clsxm('flex w-full px-4 pt-4', !isMobile && 'absolute top-0 right-0 z-10')}>
            {/* TODO refactor div to be button element */}
            {trialOnboarding === ETrialOnboardingType.TRIAL_PRINT_AND_SHARE ||
              (trialOnboarding === ETrialOnboardingType.TRIAL_EXTEND_TRIAL && (
                <div
                  className="relative w-6 h-6 cursor-pointer hover:bg-white rounded-full border border-gray-300 hover:border-gray-500 transition"
                  onClick={openPreviousDialogFromHistory}
                  aria-hidden
                  data-testid="back-dialog-button"
                >
                  <ArrowForwardIosIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-accept-dark-blue rotate-180 scale-50" />
                </div>
              ))}
            <div
              className="cursor-pointer hover:bg-white rounded ml-auto"
              onClick={removeTrialOnboardingDialog}
              aria-hidden
              data-testid="close-dialog-button"
            >
              <FiRrCrossSmall />
            </div>
          </div>
          <div className="grid md:grid-cols-2">
            {/* left side */}
            <div className="bg-white p-8 pt-0 md:pt-10 md:min-h-[520px] md:min-w-96">{renderLeftSide()}</div>
            {/* right side */}
            {renderRightSide()}
            {!isMobile && shouldShowAnimatedArrow && (
              <AnimatedArrowForOnboarding
                from={TRIAL_ONBOARDING_ARROW_START_ELEMENT_ID}
                to={TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID}
                color="#455773"
                offsetStartX={0.2}
                offsetStartY={0.8}
                offsetEndX={-0.4}
                offsetEndY={0.9}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export enum ETrialOnboardingType {
  TRIAL_ONBOADRDING = 'TRIAL_ONBOADRDING',
  TRIAL_EXTEND_TRIAL = 'TRIAL_EXTEND_TRIAL',
  TRIAL_THANKS_REVIEW = 'TRIAL_THANKS_REVIEW',
  TRIAL_PRINT_AND_SHARE = 'TRIAL_PRINT_AND_SHARE',
}

export const useDelayAfterRender = (shouldTick: boolean, duration = 5000) => {
  const [isTimeUp, setIsTimeUp] = useState(false);

  useEffect(() => {
    if (!shouldTick) {
      return;
    }
    setIsTimeUp(false);

    const timer = window.setTimeout(() => {
      setIsTimeUp(true);
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [duration, shouldTick]);

  return isTimeUp;
};

export const useTrialOnboardingDialog = (defaultType: ETrialOnboardingType | null) => {
  const { remove: removeTrialOnboardingDialog } = useModal(TrialOnboardingDialog);
  const { modalType } = useTrialPhasesConfig();
  const { state, setState } = useTrialOnboardingDialogState();
  const hasHistory = (state?.history?.length ?? 0) > 0;

  const openTrialOnboardingDialog = useHandler((overrideType?: ETrialOnboardingType) => {
    const typeToOpen = overrideType ?? defaultType ?? null;
    if (!typeToOpen) {
      return;
    }

    setState({
      ...state,
      isOpen: true,
      history: [...(state?.history ?? []), { trialOnboarding: typeToOpen, modalType }],
    });

    NiceModal.show(TrialOnboardingDialog, {
      trialOnboarding: typeToOpen,
    });
  });

  const memoizedRemoveTrialOnboardingDialog = useHandler(() => {
    setState({ ...state, isOpen: false });
    removeTrialOnboardingDialog();
  });

  const openPreviousDialogFromHistory = useHandler(() => {
    const newHistory = [...(state?.history ?? [])];
    newHistory.pop();
    const previous = newHistory[newHistory.length - 1];

    if (previous) {
      const { trialOnboarding, modalType } = previous;
      NiceModal.show(TrialOnboardingDialog, {
        trialOnboarding: trialOnboarding,
        setModalType: modalType,
      });
    }
    setState({
      ...state,
      isOpen: true,
      history: newHistory,
    });
  });

  return {
    openTrialOnboardingDialog,
    removeTrialOnboardingDialog: memoizedRemoveTrialOnboardingDialog,
    openPreviousDialogFromHistory,
    hasHistory,
  };
};
